import { Injectable } from '@angular/core';
import { DeepOmit } from 'graphql/libs/helpers/types/helpers';
import {
  pickImagesForThumb,
  pickImagesForTriplexThumb,
  triplexThumbRedrawNeeded,
  wallDecorThumbRedrawNeeded,
} from 'graphql/libs/look-book-client/src/helpers';
import { WallDecorRenderPreviewConfig } from '../../../../../photographer-client/src/app/modules/look-book/graphql';
import { groupArrayByIndex } from '../../../../../../helpers/array';
import { LookBookProductType } from 'ZalamoLibrary/src/lib/ui/look-book-front/types';
import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  from,
  map,
  of,
  retry,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import {
  AvailableWallDecorScenesGQL,
  WallDecorPieceForDisplayType,
} from 'ZalamoLibrary/src/lib/ui/look-book-front/wall-decor-front/graphql';
import { ImagesInWallDecorMap } from 'ZalamoLibrary/src/lib/ui/look-book-front/wall-decor-front/types';
import { LookBookCanvasSceneRenderBody } from 'graphql/libs/look-book-rendering/types';
import { HttpClient } from '@angular/common/http';
import { BlobStorageService } from 'ZalamoLibrary/src/lib/upload/azure/azure';
import { CustomConfigOption } from 'ZalamoLibrary/src/lib/ui/products/types';
import {
  PrivateSessionContext,
  TriplexRenderPreviewConfig,
} from '../../../../../photographer-panel/src/graphql';
import { environment } from 'ZalamoLibrary/src/lib/ui/look-book-front/enviroment/enviroment';
import { FaceDetectorService } from '../../zalamo/face-detection/face-detector.service';
import { RxjsOperators } from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';
import {
  buildRelationsMapForLookBook,
  calculateFiltersFromRelatedProducts,
  calculateProductsInPlay,
  filterAttributesByRelations,
} from '@ui/look-book-front/helpers';
import { TriplexFrontService } from '@ui/look-book-front/triplex/triplex-front/triplex-front.service';
import { UntypedFormBuilder } from '@angular/forms';
import { parseProductConfigEntry } from '@ui/products/products-front/helpers';
import { LookBookPersonalizationTypes } from '../../../../../zalamo-embed/src/app/store/graphql';
import { DecorationPreConfigForClient } from '../../../../../public-session/src/graphql';
import { ZalamoDialogService } from '@ui/zalamo-dialog/zalamo-dialog.service';
import { LookBookMissingPersonalizationPickerComponent } from '@ui/look-book-front/look-book-missing-personalization-picker/look-book-missing-personalization-picker.component';
import { PublicSessionContext } from '../../../../../../graphql/apps/photographer/src/graphql';

const imageSourceSet = new Subject();
const labs: Record<LookBookProductType, string> = {
  [LookBookProductType.WellDecor]: null,
  [LookBookProductType.Triplex]: null,
  [LookBookProductType.Tambour]: null,
  [LookBookProductType.ZalamoCalendar]: null,
};

@Injectable({
  providedIn: 'root',
})
export class LookBookProductPreviewsService {
  currentPreviews = new Map<
    LookBookProductType,
    | Omit<WallDecorRenderPreviewConfig, 'wallDecorRenderPreviewConfigId'>
    | Omit<TriplexRenderPreviewConfig, 'triplexRenderPreviewConfigId'>
  >();
  sessionOccasion: PublicSessionContext | PrivateSessionContext;
  availableTypes = new Map<LookBookProductType, boolean>();
  preConfig: Record<LookBookProductType, { min: number; max: number }> = {
    [LookBookProductType.Triplex]: { min: 3, max: 3 },
    [LookBookProductType.WellDecor]: { min: 1, max: 6 },
    [LookBookProductType.Tambour]: { min: 1, max: 6 },
    [LookBookProductType.ZalamoCalendar]: { min: 1, max: 1 },
  };

  allImages: {
    photoId: string;
    width: number;
    height: number;
    photoGroupId: string;
  }[] = [];
  limitedTriplexMaterials = [];
  personalizationPreConfig: DecorationPreConfigForClient;
  context: 'PublicSession' | 'PrivateSession';
  contextId: string = null;
  personalizationSource$: Observable<DecorationPreConfigForClient>;
  wallDecorPreviewUpdated = new Subject<{
    images: {
      crop: {
        h: number;
        w: number;
        x: number;
        y: number;
      };
      photoGroupId: string;
      photoId: string;
      wallDecorPieceId: string;
      isFlip: boolean;
    }[];
    fileName: string;
    wallDecorSceneId: string;
    accessToken: string;
  }>();
  tambourPreviewUpdated = new Subject<{
    images: {
      crop: {
        h: number;
        w: number;
        x: number;
        y: number;
      };
      photoGroupId: string;
      photoId: string;
      wallDecorPieceId: string;
      isFlip: boolean;
    }[];
    fileName: string;
    wallDecorSceneId: string;
    accessToken: string;
  }>();
  triplexPreviewUpdated = new Subject<{
    images: {
      crop: {
        h: number;
        w: number;
        x: number;
        y: number;
      };
      photoGroupId: string;
      photoId: string;
      wallDecorPieceId: string;
      isFlip: boolean;
    }[];
    fileName: string;
    value: string;
    accessToken: string;
    userDetails: {
      connector: string;
      date: string;
      decorationId: string;
      firstName: string;
      firstNameInitial: string;
      lines: string[];
      personalizationMode: LookBookPersonalizationTypes;
      secondName: string;
      secondNameInitial: string;
    };
  }>();
  userPersonalizationUpdated = new Subject<
    Partial<DecorationPreConfigForClient>
  >();
  availableScenes$ = this.availableWallDecorScenesGQL
    .fetch({})
    .pipe(
      map(
        ({ data: { getAvailableWallDecorScenes } }) =>
          getAvailableWallDecorScenes,
      ),
    );

  get nodeId(): any {
    return sessionStorage.getItem('wallDecorNodeId');
  }

  set nodeId(value: any) {
    if (!value) {
      sessionStorage.removeItem('wallDecorNodeId');
    } else {
      sessionStorage.setItem('wallDecorNodeId', value);
    }
  }

  get labs() {
    return labs;
  }

  constructor(
    private availableWallDecorScenesGQL: AvailableWallDecorScenesGQL,
    private httpClient: HttpClient,
    private blobStorageService: BlobStorageService,
    private faceDetectorService: FaceDetectorService,
    private triplexFrontService: TriplexFrontService,
    private fb: UntypedFormBuilder,
    private zalamoDialogService: ZalamoDialogService,
  ) {
    console.log(`


    Previews constructed


    `);
  }

  enableType(
    type: LookBookProductType,
    currentPreview:
      WallDecorRenderPreviewConfig | TriplexRenderPreviewConfig | null,
    labId: string,
  ) {
    this.availableTypes.set(type, true);
    this.currentPreviews.set(type, currentPreview);
    this.labs[type] = labId;
  }

  imagesInBasketSource(images: Observable<string[]>) {
    imageSourceSet.next(undefined);
    images
      .pipe(
        filter((v) => !!v.length),
        tap((x) => console.log(x)),
        takeUntil(imageSourceSet),
        debounceTime(2000),
        map((r) => r.sort().join(',')),
        tap((x) => console.log(x)),
        distinctUntilChanged(),
        map((images) => images.split(',')),
        switchMap((availableImages) => {
          console.log('SHOW PREVIEWS');
          return combineLatest(
            [...this.availableTypes.keys()].map((productType) =>
              this.getPreview(
                this.currentPreviews.get(productType),
                availableImages,
                this.preConfig[productType].min,
                this.preConfig[productType].max,
                productType,
              ),
            ),
          );
        }),
      )
      .subscribe();
  }

  getPreview(
    current:
      | DeepOmit<
          Omit<WallDecorRenderPreviewConfig, 'wallDecorRenderPreviewConfigId'>,
          '__typename'
        >
      | DeepOmit<
          Omit<TriplexRenderPreviewConfig, 'triplexRenderPreviewConfigId'>,
          '__typename'
        >,
    availableImages: string[],
    minImages = 1,
    maxImages = 3,
    productType: LookBookProductType,
  ): Observable<string> {
    if (availableImages.length === 0) {
      this.wallDecorPreviewUpdated.next(null);
      return of(null);
    }
    const currentVersionBelowLimit = current?.images?.length < minImages;
    const currentVersionCanHaveMore =
      availableImages.length > current?.images?.length &&
      availableImages.length > minImages &&
      current?.images?.length < maxImages;
    let rerenderingRequired = true;
    const indexedPhotos = groupArrayByIndex(this.allImages, 'photoId');

    switch (productType) {
      case LookBookProductType.WellDecor:
        rerenderingRequired = wallDecorThumbRedrawNeeded(
          groupArrayByIndex(current?.images ?? [], 'wallDecorPieceId'),
          availableImages,
        );
        break;
      case LookBookProductType.Tambour:
        rerenderingRequired = wallDecorThumbRedrawNeeded(
          groupArrayByIndex(current?.images ?? [], 'wallDecorPieceId'),
          availableImages,
        );
        break;
      case LookBookProductType.Triplex:
        rerenderingRequired = triplexThumbRedrawNeeded(
          groupArrayByIndex(current?.images ?? [], 'wallDecorPieceId'),
          availableImages,
          indexedPhotos,
        );
        break;
    }
    let isTokenTimeValid = false;
    if (current?.accessToken) {
      const params = new URLSearchParams(current.accessToken);
      isTokenTimeValid = new Date() < new Date(params.get('se'));
    }
    if (
      !current ||
      !current.fileName ||
      currentVersionCanHaveMore ||
      rerenderingRequired ||
      (current.fileName && !isTokenTimeValid)
    ) {
      console.log('GENERATE NEW PREVIEW', productType);

      switch (productType) {
        case LookBookProductType.Triplex:
          return rerenderingRequired || (current?.fileName && !isTokenTimeValid)
            ? this.previewForTriplex(availableImages)
            : of(null);
        case LookBookProductType.WellDecor:
          return this.availableScenes$.pipe(
            switchMap((scenes) => {
              const filtered = scenes.filter(
                (scene) =>
                  scene.labs.includes(
                    this.labs[LookBookProductType.WellDecor],
                  ) &&
                  scene.pieces.some(
                    ({ type }) => type === WallDecorPieceForDisplayType.Canvas,
                  ),
              );
              const pickedScene = pickImagesForThumb(
                this.allImages.filter(({ photoId }) =>
                  availableImages.includes(photoId),
                ),
                filtered,
                Math.min(
                  availableImages.length,
                  this.preConfig[productType].max,
                ),
              );
              if (!pickedScene.matchingScene) {
                return of(null);
              }

              const indexedPieces = groupArrayByIndex(
                pickedScene.matchingScene.pieces,
                'WallDecorPieceForDisplayId',
              );
              return combineLatest(
                Object.values(pickedScene.pieces).map(
                  ({ photoId, WallDecorPieceForDisplayId }) => {
                    const currentValue =
                      indexedPieces[WallDecorPieceForDisplayId];
                    const containerRatio = currentValue.flip
                      ? currentValue.imageHeight / currentValue.imageWidth
                      : currentValue.imageWidth / currentValue.imageHeight;
                    console.log(
                      'DETECT FACE FOR CONTAIENR RATIO ',
                      containerRatio,
                    );
                    return this.faceDetectorService
                      .detectFacesByPhotoId(photoId, containerRatio)
                      .pipe(map((faces) => ({ photoId, faces })));
                  },
                ),
              ).pipe(
                map((elements) =>
                  // @ts-ignore
                  Object.fromEntries(
                    elements.map(({ photoId, faces }) => [photoId, faces]),
                  ),
                ),
                switchMap((faces) => {
                  console.log('picked scene', pickedScene);
                  // @ts-ignore
                  const images = Object.fromEntries(
                    Object.entries(pickedScene.pieces).map(([index, value]) => {
                      return [
                        index,
                        {
                          ...value,
                          photoGroupId:
                            indexedPhotos[value.photoId].photoGroupId,
                          isFlip: value.isFlip,
                          crop: faces[value.photoId].maxSquare,
                        },
                      ];
                    }),
                  );
                  return this.previewForWallDecor(
                    pickedScene.matchingScene,
                    images,
                  ).pipe(
                    catchError(() => {
                      this.nodeId = null;
                      return this.previewForWallDecor(
                        pickedScene.matchingScene,
                        images,
                      );
                    }),
                    retry(10),
                  );
                }),
              );
            }),
            first(),
          );
        case LookBookProductType.Tambour:
          return this.availableScenes$.pipe(
            switchMap((scenes) => {
              const pickedScene = pickImagesForThumb(
                this.allImages.filter(({ photoId }) =>
                  availableImages.includes(photoId),
                ),
                scenes.filter(
                  (scene) =>
                    scene.labs.includes(
                      this.labs[LookBookProductType.Tambour],
                    ) &&
                    !!scene.pieces.find(
                      ({ type }) =>
                        type === WallDecorPieceForDisplayType.Tambour,
                    ),
                ),
                Math.min(
                  availableImages.length,
                  this.preConfig[productType].max,
                ),
              );
              if (!pickedScene.matchingScene) {
                return of(null);
              }
              return combineLatest(
                Object.values(pickedScene.pieces).map(
                  ({ photoId, WallDecorPieceForDisplayId }) => {
                    return this.faceDetectorService
                      .detectFacesByPhotoId(photoId)
                      .pipe(map((faces) => ({ photoId, faces })));
                  },
                ),
              ).pipe(
                map((elements) =>
                  // @ts-ignore
                  Object.fromEntries(
                    elements.map(({ photoId, faces }) => [photoId, faces]),
                  ),
                ),
                switchMap((faces) => {
                  // @ts-ignore
                  const images = Object.fromEntries(
                    Object.entries(pickedScene.pieces).map(([index, value]) => {
                      const imageRatio =
                        indexedPhotos[value.photoId].width /
                        indexedPhotos[value.photoId].height;

                      return [
                        index,
                        {
                          ...value,
                          photoGroupId:
                            indexedPhotos[value.photoId].photoGroupId,
                          isFlip: value.isFlip,
                          crop: faces[value.photoId].maxSquare,
                        },
                      ];
                    }),
                  );
                  return this.previewForTambour(
                    pickedScene.matchingScene,
                    images,
                  ).pipe(
                    catchError(() => {
                      this.nodeId = null;
                      return this.previewForTambour(
                        pickedScene.matchingScene,
                        images,
                      );
                    }),
                    retry(10),
                  );
                }),
              );
            }),
            first(),
          );
      }
    }
    console.log('NO PREVIEW NEEDED');
    return of(null);
  }

  previewForWallDecor(scene, imageMap: ImagesInWallDecorMap) {
    const size = Math.min(
      1280,
      Math.ceil((720 * window.devicePixelRatio) / 80) * 80,
    );
    const body: LookBookCanvasSceneRenderBody & { isPreview: boolean } = {
      color: localStorage.wallColor ?? '#FFFFFF',
      sceneId: scene.wallDecorSceneForDisplayId,
      nodeId: this.nodeId,
      images: scene.pieces.reduce((prevValue, currentValue) => {
        if (imageMap[currentValue.WallDecorPieceForDisplayId]) {
          prevValue[currentValue.WallDecorPieceForDisplayId] =
            imageMap[currentValue.WallDecorPieceForDisplayId];
        }
        return prevValue;
      }, {} as ImagesInWallDecorMap),
      size,
      isPreview: true,
      pieces: [
        'front',
        ...scene.pieces.map(
          ({ WallDecorPieceForDisplayId }) => WallDecorPieceForDisplayId,
        ),
      ],
    };
    return this.httpClient
      .post<{
        fileName: string;
        token: string;
        ui: { overlayDom; overlayLayers; nodeId: string };
      }>(environment.canvasScenesUrl, body, { responseType: 'json' })
      .pipe(
        tap((v) => {
          this.currentPreviews.set(LookBookProductType.WellDecor, {
            wallDecorSceneId: scene.wallDecorSceneForDisplayId,
            fileName: v.fileName,
            accessToken: v.token,
            color: localStorage.wallColor ?? '#FFFFFF',
            images: Object.entries(imageMap).map(
              ([
                wallDecorPieceId,
                { photoId, photoGroupId, crop, isFlip },
              ]) => ({
                wallDecorPieceId,
                photoId,
                photoGroupId,
                crop,
                isFlip,
              }),
            ),
          });
          this.wallDecorPreviewUpdated.next({
            wallDecorSceneId: scene.wallDecorSceneForDisplayId,
            fileName: v.fileName,
            accessToken: v.token,
            images: Object.entries(imageMap).map(
              ([
                wallDecorPieceId,
                { photoId, photoGroupId, crop, isFlip },
              ]) => ({
                wallDecorPieceId,
                photoId,
                photoGroupId,
                crop,
                isFlip,
              }),
            ),
          });
        }),
        map(({ fileName }) => fileName),
      );
  }

  previewForTambour(scene, imageMap: ImagesInWallDecorMap) {
    const size = Math.min(
      1280,
      Math.ceil((720 * window.devicePixelRatio) / 80) * 80,
    );
    const body: LookBookCanvasSceneRenderBody & { isPreview: boolean } = {
      color: localStorage.wallColor ?? '#FFFFFF',
      sceneId: scene.wallDecorSceneForDisplayId,
      nodeId: this.nodeId,
      images: scene.pieces.reduce((prevValue, currentValue) => {
        if (imageMap[currentValue.WallDecorPieceForDisplayId]) {
          prevValue[currentValue.WallDecorPieceForDisplayId] =
            imageMap[currentValue.WallDecorPieceForDisplayId];
        }
        return prevValue;
      }, {} as ImagesInWallDecorMap),
      size,
      isPreview: true,
      pieces: [
        'front',
        ...scene.pieces.map(
          ({ WallDecorPieceForDisplayId }) => WallDecorPieceForDisplayId,
        ),
      ],
    };
    return this.httpClient
      .post<{
        fileName: string;
        token: string;
        ui: { overlayDom; overlayLayers; nodeId: string };
      }>(environment.canvasScenesUrl, body, { responseType: 'json' })
      .pipe(
        tap((v) => {
          this.currentPreviews.set(LookBookProductType.Tambour, {
            wallDecorSceneId: scene.wallDecorSceneForDisplayId,
            fileName: v.fileName,
            accessToken: v.token,
            color: localStorage.wallColor ?? '#FFFFFF',
            images: Object.entries(imageMap).map(
              ([
                wallDecorPieceId,
                { photoId, photoGroupId, crop, isFlip },
              ]) => ({
                wallDecorPieceId,
                photoId,
                photoGroupId,
                crop,
                isFlip,
              }),
            ),
          });
          this.tambourPreviewUpdated.next({
            wallDecorSceneId: scene.wallDecorSceneForDisplayId,
            fileName: v.fileName,
            accessToken: v.token,
            images: Object.entries(imageMap).map(
              ([
                wallDecorPieceId,
                { photoId, photoGroupId, crop, isFlip },
              ]) => ({
                wallDecorPieceId,
                photoId,
                photoGroupId,
                crop,
                isFlip,
              }),
            ),
          });
        }),
        map(({ fileName }) => fileName),
      );
  }

  previewForTriplex(availableImages: string[]) {
    const result = pickImagesForTriplexThumb(
      this.allImages.filter(({ photoId }) => availableImages.includes(photoId)),
    );

    const model = this.fb.group({});

    return combineLatest([
      model.valueChanges.pipe(
        filter((v) => Object.keys(v).length > 0),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        debounceTime(10),
        startWith({}),
      ),
      this.triplexFrontService.getDetails(this.context, this.contextId),
    ]).pipe(
      map(
        ([
          currentValue,
          {
            labsInPlay,
            productsMap,
            zalamoStandaloneTriplex: { config },
          },
        ]) => {
          let filtered = [];
          const relationsMap = buildRelationsMapForLookBook(JSON.parse(config));
          const productsToRemove = [];
          let localProductsInPlay = [];

          const { mappedValues, productsInPlay } = calculateProductsInPlay(
            labsInPlay,
            productsMap,
            currentValue,
            filtered,
            relationsMap,
            1,
          );
          localProductsInPlay = productsInPlay;
          parseProductConfigEntry(
            JSON.parse(config),
            model,
            { ...currentValue },
            filtered,
            true,
            false,
            true,
          );
          filtered = filtered.filter(({ active }) => active);
          const filtersFromRelatedProducts =
            calculateFiltersFromRelatedProducts(
              localProductsInPlay,
              mappedValues,
              currentValue,
              productsToRemove,
            );
          // const filterForPrevLevel = calculateFiltersFromRelatedProducts(
          //   this.prevProductsInPlay,
          //   this.prevMappedValues,
          //   currentValue,
          //   []
          // );
          filterAttributesByRelations(
            filtered,
            relationsMap,
            [],
            filtersFromRelatedProducts,
            0,
          );
          const options = filtered.filter(({ active }) => active);
          const sizeAttribute = options.find(
            ({ type, active }) => type === 'size' && active,
          );
          const sortedOptions = sizeAttribute.options
            .filter(({ active }) => active)
            .sort((a, b) => b.width * b.height - a.width * b.height);
          let size: CustomConfigOption = null;
          if (result.isVertical) {
            size = sortedOptions.find(({ width, height }) => height > width);
          } else {
            size = sortedOptions.find(({ width, height }) => height <= width);
          }
          if (!size) {
            size = sortedOptions[0];
          }
          const materialAttribute = options.find(
            ({ customId }) => customId === 'cl5cc7v7k000g359p7lb4nx5n',
          );
          if (currentValue[sizeAttribute.customId] !== size.configId) {
            model.patchValue({
              [sizeAttribute.customId]: size.configId,
            });
          }
          if (!materialAttribute) {
            return null;
          }
          const availableMaterials = materialAttribute?.options.filter(
            ({ configId, relations, active }) =>
              active &&
              ((active && !this.limitedTriplexMaterials.length) ||
                !relations ||
                relations.find(
                  ({ value, id }) =>
                    this.limitedTriplexMaterials.includes(value) ||
                    this.limitedTriplexMaterials.includes(id),
                )),
          );
          const materialForPreview = availableMaterials[0].configId;
          const value: Record<string, string> = {};
          value[sizeAttribute.customId] = size.configId;
          value[materialAttribute?.customId] = materialForPreview;
          if (currentValue[materialAttribute.customId] !== materialForPreview) {
            model.patchValue({
              [materialAttribute.customId]: materialForPreview,
            });
          }
          const possibleDecorationParam = filtered.find(
            ({ customId }) => customId === 'cl5cdf8pl000o359pjd6bss5b',
          );
          if (possibleDecorationParam) {
            const possibleDecorationOption =
              possibleDecorationParam.options.find(
                ({ active, decorationId }) => active && decorationId,
              );
            if (possibleDecorationOption) {
              if (
                currentValue[possibleDecorationParam.customId] !==
                possibleDecorationOption.configId
              ) {
                model.patchValue({
                  [possibleDecorationParam.customId]:
                    possibleDecorationOption.configId,
                });
              }
            }
          }
          value.cl5cdf8pl000o359pjd6bss5b = '';
          let decorationId = '';
          if (currentValue.cl5cdf8pl000o359pjd6bss5b) {
            decorationId = possibleDecorationParam.options.find(
              ({ configId }) =>
                configId === currentValue.cl5cdf8pl000o359pjd6bss5b,
            )?.decorationId;
          }
          return {
            size,
            decorationId,
            currentValue,
          };
        },
      ),
      RxjsOperators.filterTruthy(),
      debounceTime(100),
      tap((x) => console.log(x)),
      switchMap(
        ({
          size,
          ...prev
        }: {
          decorationId: string;
          currentValue: Record<string, any>;
          size;
        }) => {
          const indexedPhotos = groupArrayByIndex(this.allImages, 'photoId');

          // @ts-ignore

          return combineLatest(
            [0, 1, 2].map((index) => {
              const containerRatio = size.width / size.height;
              return this.faceDetectorService
                .detectFacesByPhotoId(
                  indexedPhotos[result.images[index].photoId].photoId,
                  containerRatio,
                )
                .pipe(map((faces) => ({ faces })));
            }),
          ).pipe(
            map((elements) =>
              // @ts-ignore
              ({
                ...prev,
                images: elements.map(({ faces }, index) => {
                  return {
                    wallDecorPieceId: String(index),
                    photoGroupId:
                      indexedPhotos[result.images[index].photoId].photoGroupId,
                    photoId:
                      indexedPhotos[result.images[index].photoId].photoId,
                    isFlip: false,
                    crop: faces.maxSquare,
                  };
                }),
              }),
            ),
          );
        },
      ),
      switchMap(({ decorationId, currentValue, images }) => {
        if (!decorationId) {
          return of({ decorationId, currentValue, images, userDetails: null });
        }

        return this.personalizationSource$.pipe(
          first(),
          switchMap((personalization) => {
            const final = {
              ...this.personalizationPreConfig,
              ...personalization,
            };
            if (final.firstName) {
              return of(final);
            }
            const ref = this.zalamoDialogService.openFull(
              LookBookMissingPersonalizationPickerComponent,
              {
                isBare: true,
              },
            );
            ref.componentInstance.sessionOccasion = this.sessionOccasion;
            return ref.componentInstance.saved.pipe(
              tap((userProvidedPersonalization) => {
                ref.close();
                this.userPersonalizationUpdated.next(
                  userProvidedPersonalization,
                );
              }),
              map((userProvidedPersonalization) => ({
                ...this.personalizationPreConfig,
                ...personalization,
                ...(userProvidedPersonalization
                  ? userProvidedPersonalization
                  : {}),
              })),
              map((final) => ({
                ...final,
                firstNameInitial:
                  final.firstName?.charAt(0).toUpperCase() || '',
                secondNameInitial: final.secondName?.charAt(0).toUpperCase(),
              })),
            );
          }),

          map((userDetails) => ({
            decorationId,
            currentValue,
            images,

            userDetails,
          })),
        );
      }),
      switchMap(({ decorationId, currentValue, images, userDetails }) => {
        const body = {
          currentValue,
          productId: 'cl5b6rbph000y359poohndopk',
          currentConfigOptionId: 'cl5cc7v7k000g359p7lb4nx5n',
          userDetails: decorationId ? { ...userDetails, decorationId } : null,
          scene: 1,
          images: images.map((v) => ({
            ...v,
            pieceId: v.wallDecorPieceId,
          })),
        };
        return this.httpClient
          .post<{ token: string; fileName: string }>(
            `${environment.renderTriplex}`,
            body,
            {
              responseType: 'json',
              withCredentials: false,
            },
          )
          .pipe(
            filter((v) => !!v?.token),
            tap((v) => {
              this.triplexPreviewUpdated.next({
                value: JSON.stringify(body.currentValue),
                images,
                fileName: v.fileName,
                accessToken: v.token,
                userDetails: body.userDetails,
              });
            }),
            switchMap((c) =>
              from(
                this.blobStorageService
                  .downloadFile(
                    {
                      storageUri:
                        'https://newzalamomiddles.blob.core.windows.net',
                      storageAccessToken: c.token,
                      container: 'zalamo',
                      filename: null,
                    },
                    'zalamo',
                    c.fileName,
                  )
                  .then(
                    (r) => r.blobBody,
                    () => {
                      console.log('THUMB DOWNLOAD FAIL');
                      return null;
                    },
                  )
                  .then((v) => {
                    return URL.createObjectURL(v);
                  }),
              ),
            ),

            tap((x) =>
              console.log(
                `

            TRIPLEX PREVV

            `,
                x,
              ),
            ),
          );
      }),
    );
  }
}
